import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(() => {
        // Récupérer le thème depuis localStorage ou utiliser 'light' par défaut
        const savedDisplay = localStorage.getItem('settings_display');
        if (savedDisplay) {
            try {
                const parsed = JSON.parse(savedDisplay);
                return parsed.theme === 'dark' ? 'dark' : 'light';
            } catch {
                return 'light';
            }
        }
        return 'light';
    });

    useEffect(() => {
        // Appliquer la classe 'dark' sur le document.body
        if (theme === 'dark') {
            document.body.classList.add('dark-theme');
            document.body.classList.remove('light-theme');
            document.documentElement.setAttribute('data-theme', 'dark');
        } else {
            document.body.classList.add('light-theme');
            document.body.classList.remove('dark-theme');
            document.documentElement.setAttribute('data-theme', 'light');
        }
    }, [theme]);

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
        // Mettre à jour localStorage
        const savedDisplay = localStorage.getItem('settings_display');
        const displaySettings = savedDisplay ? JSON.parse(savedDisplay) : {};
        displaySettings.theme = newTheme;
        localStorage.setItem('settings_display', JSON.stringify(displaySettings));
    };

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
